// util/analytics/google.service.ts

import { isDoNotSellSignalActive, normalizeUserForDL } from './analytics.service';

/* -------------------------------------------------------------------------- */
/*                                  Types                                     */
/* -------------------------------------------------------------------------- */

const isProd = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production';

type AnyRecord = Record<string, any>;

type GA4Item = {
  item_id: string;
  item_name: string;
  affiliation?: string;
  coupon?: string;
  discount?: number;
  index?: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  location_id?: string;
  price?: number;
  promotion_id?: string;
  promotion_name?: string;
  quantity?: number;
};

type GA4Purchase = {
  event: 'purchase';
  ecommerce: {
    transaction_id: string;
    value?: number;
    tax?: number;
    shipping?: number;
    currency?: string;
    coupon?: string;
    customer_type?: string;
    items: GA4Item[];
  };
};

/* -------------------------------------------------------------------------- */
/*                                Utilities                                   */
/* -------------------------------------------------------------------------- */

const compact = <T extends AnyRecord>(obj: T): T =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) =>
        v !== undefined &&
        v !== null &&
        !(typeof v === 'number' && Number.isNaN(v))
    )
  ) as T;

const affiliationFromSellers = (sellers: any): string | undefined => {
  if (!sellers) return undefined;
  if (Array.isArray(sellers)) {
    const names = sellers
      .map((s) => (typeof s === 'string' ? s : s?.name || s?.displayName))
      .filter(Boolean);
    if (names.length) return names.join(', ');
  } else if (typeof sellers === 'string') {
    return sellers;
  } else if (typeof sellers === 'object' && (sellers.name || sellers.displayName)) {
    return sellers.name || sellers.displayName;
  }
  return undefined;
};

const mapItemToGA4 = (
  raw: any,
  index: number,
  defaultAffiliation?: string
): GA4Item => {
  // CHANGED: Prioritize Firestore doc ID (id) over SKU for Meta catalog matching
  // Product feed uses Firestore doc IDs, so all tracking events must use the same ID
  const id =
    raw?.id || raw?.item_id || raw?.sku || raw?.external_id?.toString();
  const name = raw?.title || raw?.item_name || raw?.name || raw?.slug;
  const brand = raw?.brand || raw?.item_brand;
  const categories: string[] =
    Array.isArray(raw?.categories) ? raw.categories.slice(0, 5) :
    Array.isArray(raw?.item_categories) ? raw.item_categories.slice(0, 5) : [];

  const price =
    (typeof raw?.price === 'number' ? raw.price : undefined) ??
    (typeof raw?.unit_price === 'number' ? raw.unit_price : undefined) ??
    (typeof raw?.amount === 'number' ? raw.amount : undefined);

  const quantity =
    (typeof raw?.quantity === 'number' ? raw.quantity : undefined) ??
    (typeof raw?.qty === 'number' ? raw.qty : undefined) ??
    1;

  const variant = raw?.condition || raw?.variant || raw?.item_variant;

  const catFields: Partial<GA4Item> = {};
  if (categories[0]) catFields.item_category = categories[0];
  if (categories[1]) catFields.item_category2 = categories[1];
  if (categories[2]) catFields.item_category3 = categories[2];
  if (categories[3]) catFields.item_category4 = categories[3];
  if (categories[4]) catFields.item_category5 = categories[4];

  return compact({
    item_id: String(id ?? ''),
    item_name: String(name ?? ''),
    affiliation: raw?.affiliation || defaultAffiliation,
    coupon: raw?.coupon,
    discount: typeof raw?.discount === 'number' ? raw.discount : undefined,
    index,
    item_brand: brand,
    ...catFields,
    item_list_id: raw?.item_list_id,
    item_list_name: raw?.item_list_name,
    item_variant: variant,
    location_id: raw?.location_id,
    price,
    promotion_id: raw?.promotion_id,
    promotion_name: raw?.promotion_name,
    quantity,
  });
};

const toGA4Purchase = (payload: AnyRecord): GA4Purchase | null => {
  if (!payload) return null;

  const tx = payload.transaction ?? {};
  const currency = payload.currency ?? 'USD';
  const coupon = payload.coupon;
  const affiliation = affiliationFromSellers(tx.sellers);

  const rawItems: any[] = Array.isArray(payload.items) ? payload.items : [];
  const items: GA4Item[] = rawItems
    .map((it, idx) => mapItemToGA4(it, idx, affiliation))
    .filter((it) => it.item_id && it.item_name); // avoid empty rows

  const value =
    typeof tx.total === 'number' && !Number.isNaN(tx.total)
      ? tx.total
      : items.reduce((sum, it) => sum + (it.price ?? 0) * (it.quantity ?? 1), 0);

  const ecommerce = compact({
    transaction_id: String(tx.id ?? payload.orderId ?? payload.id ?? ''),
    value,
    tax: typeof tx.tax === 'number' ? tx.tax : undefined,
    shipping: typeof tx.shipping === 'number' ? tx.shipping : undefined,
    currency,
    coupon,
    items,
  });

  if (!ecommerce.transaction_id) return null;

  return {
    event: 'purchase',
    ecommerce,
  };
};

// normalizeUserForDL moved to analytics.service.ts (F2 / GB #1373) so the
// Purchase CAPI path here and the Purchase Manual Advanced Matching path in
// logPurchase() share one normalizer instead of two copies. No logic change.

/* -------------------------------------------------------------------------- */
/*                         Idempotent purchase push                           */
/* -------------------------------------------------------------------------- */

type PurchaseOptions = {
  /** If true, bypass idempotency guards (useful for QA). Default: false */
  allowRepeat?: boolean;
  /** If true, also clear ecommerce after the push. Default: false */
  clearAfter?: boolean;
  /** Override page context in the dataLayer push (e.g., when firing before navigation) */
  pageContext?: {
    page_location?: string;
    page_path?: string;
    page_title?: string;
  };
  /**
   * F1 fix (post-merge review, GB #1373). When provided, wired as GTM's
   * documented `eventCallback` on the 'purchase' dataLayer push
   * (https://developers.google.com/tag-platform/tag-manager/web/datalayer#eventcallback),
   * with `eventTimeout` set to `eventTimeoutMs` (default 2000, matching
   * GTM's own default). GTM invokes eventCallback once every tag triggered
   * by THIS push has finished firing — including the GA4/Ads/Meta CAPI
   * relay to Stape, which is a fetch(), not a sendBeacon(), so it CAN be
   * aborted by an immediate navigation — or after eventTimeoutMs, whichever
   * comes first. Also invoked immediately (before this function returns)
   * from every early-return path below (GPC blocked, invalid payload,
   * already sent): in those cases nothing is pushed, so no GTM callback
   * will ever fire naturally, and a caller awaiting this must not hang.
   * Use `navigateAfterFlush()` below rather than wiring this directly — it
   * also covers the case where GTM itself never loaded at all (ad blocker,
   * container blocked), which eventTimeout alone cannot, since there's no
   * GTM processor present to honor it.
   */
  onTagsSettled?: () => void;
  eventTimeoutMs?: number;
};

const sentIds = new Set<string>(); // process-lifetime guard (tab scope)
const keyFor = (txId: string) => `ga4_purchase_${txId}`;

export const googleDataLayerPurchase = (
  payload: AnyRecord,
  opts: PurchaseOptions = {}
) => {
  if (typeof window === 'undefined') { opts.onTagsSettled?.(); return; } // SSR-safe
  if (!isProd) { opts.onTagsSettled?.(); return; } // Block purchase dataLayer events on non-production
  const dl: any[] | undefined = (window as any).dataLayer;
  if (!Array.isArray(dl)) { opts.onTagsSettled?.(); return; }

  const ga4 = toGA4Purchase(payload);
  if (!ga4) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[googleDataLayerPurchase] Invalid payload or missing transaction_id',
        payload
      );
    }
    opts.onTagsSettled?.();
    return;
  }

  const txId = ga4.ecommerce.transaction_id;
  const storageKey = keyFor(txId);

  // Idempotency guards unless explicitly overridden
  if (!opts.allowRepeat) {
    if (sentIds.has(txId)) {
      opts.onTagsSettled?.();
      return;
    }
    try {
      if (window.sessionStorage?.getItem(storageKey)) {
        opts.onTagsSettled?.();
        return;
      }
    } catch {
      /* ignore */
    }
  }

  // Strip Enhanced Conversions PII (user_email, user_phone, etc.) when GPC is active.
  // Basic conversion data (transaction_id, value, items) still flows to Google Ads.
  // This satisfies CCPA: no PII "sharing" for ad matching, but conversion counting continues.
  const gpcActive = isDoNotSellSignalActive();
  const userFields = gpcActive
    ? null
    : normalizeUserForDL((payload as any)?.customer);
  // Firebase UID as external_id for Meta CAPI user matching (Stape auto-hashes via SHA-256)
  const userId = !gpcActive && (payload as any)?.userId;
  const externalId = typeof userId === 'string' && userId ? { external_id: userId } : {};

  // Clear before push (common GA4 practice)
  dl.push({ ecommerce: null });

  // === Primary dataLayer event for GTM (GA4 + Ads EC) ===
  dl.push({
    event: 'purchase',
    event_id: `purchase_${txId}`, // Meta dedup: matches fbq eventID format, enables CAPI dedup
    ecommerce: ga4.ecommerce,
    // Mirror items at top level to make GTM mapping dead simple when needed
    items: ga4.ecommerce.items,
    // GB #1410 (I2): mirrors `items` above. transaction_id lives correctly
    // under ecommerce already — this is a defensive top-level copy in case
    // the GTM container's GA4 Purchase tag variable reads the top level
    // instead of ecommerce.transaction_id (see PR body / GTM runbook).
    transaction_id: txId,
    // Custom dimension for checkout shipping analysis (#1105)
    ...(payload.shipping_type ? { shipping_type: payload.shipping_type } : {}),
    ...(userFields || {}),
    ...externalId,
    // Signal GPC/manual opt-out to Stape so it can block Meta CAPI for
    // non-GPC browsers (localStorage opt-out has no Sec-GPC header)
    ...(gpcActive ? { gpc_active: true } : {}),
    ...(opts.pageContext || {}),
    // F1 fix (post-merge review, GB #1373) — see the onTagsSettled doc
    // comment on PurchaseOptions above.
    ...(opts.onTagsSettled
      ? { eventCallback: opts.onTagsSettled, eventTimeout: opts.eventTimeoutMs ?? 2000 }
      : {}),
  });

  if (opts.clearAfter) dl.push({ ecommerce: null });

  // ===== Vendor bridge: non-GTM tools (Omnisend/Metricool) can listen safely =====
  try {
    const vendorPayload = {
      order: {
        id: ga4.ecommerce.transaction_id,
        value: ga4.ecommerce.value,
        currency: ga4.ecommerce.currency,
        tax: ga4.ecommerce.tax,
        shipping: ga4.ecommerce.shipping
      },
      items: ga4.ecommerce.items.map(it => ({
        id: it.item_id,
        name: it.item_name,
        price: it.price,
        quantity: it.quantity,
        brand: it.item_brand,
        category: it.item_category,
        variant: it.item_variant
      })),
      user: userFields || {}
    };

    // 1) CustomEvent any script can subscribe to
    window.dispatchEvent(new CustomEvent('gf:purchase', { detail: vendorPayload }));
    // 2) Global snapshot (simple pull API)
    (window as any).gfPurchase = vendorPayload;
  } catch {
    /* ignore */
  }
  // ==============================================================================

  // Mark as sent
  sentIds.add(txId);
  try {
    window.sessionStorage?.setItem(storageKey, '1');
  } catch {
    /* ignore */
  }
};

/**
 * F1/F2 fix (TRIPLE-CHECK-corrected, post-merge review, GB #1373). Fires
 * the purchase dataLayer push and navigates (hard nav — assign() OR
 * reload(), caller's choice via the `navigate` callback) once its GTM tags
 * have settled AND every promise in `alsoWaitFor` has settled — OR once
 * `timeoutMs` elapses, whichever comes first. `navigate()` fires EXACTLY
 * ONCE, no matter how many signals arrive or in what order.
 *
 * CORRECTED DESIGN (this replaces an earlier version of this fix that a
 * TRIPLE CHECK caught as subtly wrong): eventCallback AND eventTimeout are
 * BOTH GTM features. If GTM itself never loads — ad blocker, blocked
 * container, a restrictive network — dataLayer is just a plain array
 * nothing ever reads; NEITHER eventCallback NOR its eventTimeout ever
 * fires, so a design that relies on GTM's own eventTimeout as "the"
 * guarantee can hang checkout forever for exactly those users. The actual
 * guarantee here is OUR OWN JS-side setTimeout (`ownTimer` below),
 * independent of GTM entirely — it fires `navigate()` at `timeoutMs`
 * regardless of whether GTM exists, ever loaded, or ever calls back.
 * eventCallback is purely an optimization that lets a fast network / fast
 * GTM navigate sooner than the deadline; it is never load-bearing for
 * correctness.
 *
 * GTM multi-container note: this site runs TWO containers (ours, plus
 * TIE's loader), so eventCallback can fire once PER CONTAINER for the same
 * push. The `navigated` once-flag absorbs that automatically — whichever
 * signal (a container's eventCallback, the `alsoWaitFor` promises all
 * settling, or `ownTimer`) reaches `fireOnce()` first wins; every
 * subsequent call is a no-op.
 *
 * Use this (not the bare googleDataLayerPurchase()) at any call site that
 * navigates away immediately after firing the purchase event — see
 * CheckoutProcessV3.tsx / ExpressCheckoutButton.tsx's guest hard-nav path,
 * and both order-confirmation pages' redirect-return reload (F2). For
 * every other call site (the normal SPA-navigation path), the bare
 * fire-and-forget googleDataLayerPurchase() remains correct and cheaper —
 * client-side navigation doesn't abort in-flight requests the way a hard
 * nav does.
 *
 * KNOWN RESIDUAL, documented rather than silently accepted (GB #1373 /
 * #1129 — filed as a follow-up, not built here): a slow connection can
 * still lose whatever is in `alsoWaitFor` — e.g. CheckoutProcessV3.tsx's
 * markConverted() write — since `ownTimer` navigates at `timeoutMs`
 * regardless of what's still pending. The durable fix is server-side (a
 * sendBeacon-backed mark-converted API route, or setting `converted` from
 * the payment-success webhook path instead of client JS) — out of scope
 * for a client-side timing fix; a new checkout API route is Tier 1 scope
 * needing its own review gates.
 */
export function navigateAfterFlush(
  navigate: () => void,
  opts: {
    purchasePayload: AnyRecord;
    pageContext?: PurchaseOptions['pageContext'];
    /** Passed through to googleDataLayerPurchase — see its own doc comment. */
    clearAfter?: boolean;
    alsoWaitFor?: Promise<unknown>[];
    timeoutMs?: number;
  }
): void {
  const timeoutMs = opts.timeoutMs ?? 500;
  let navigated = false;
  const fireOnce = () => {
    if (navigated) return;
    navigated = true;
    navigate();
  };

  // THE guarantee — independent of GTM, dataLayer, or anything else.
  const ownTimer = setTimeout(fireOnce, timeoutMs);

  const tagsSettled = new Promise<void>((resolve) => {
    googleDataLayerPurchase(opts.purchasePayload, {
      pageContext: opts.pageContext,
      clearAfter: opts.clearAfter,
      onTagsSettled: resolve,
      eventTimeoutMs: timeoutMs,
    });
  });

  Promise.all([tagsSettled, ...(opts.alsoWaitFor ?? [])])
    .then(() => {
      clearTimeout(ownTimer);
      fireOnce();
    })
    .catch(() => {
      // Codex P2 (final round, GB #1373): if googleDataLayerPurchase()
      // (or, less likely, one of the caller's alsoWaitFor promises) throws
      // synchronously, tagsSettled's executor rejects — a Promise
      // constructor turns a synchronous throw inside its executor into a
      // rejected promise automatically — which rejects this Promise.all.
      // Without this .catch(), that would (a) emit an unhandled promise
      // rejection, and (b) silently leave navigation to wait out the FULL
      // ownTimer deadline instead of firing immediately, since only the
      // .then() branch above ever called fireOnce() early. A broken/hostile
      // dataLayer push must never strand a guest on checkout any longer
      // than necessary — navigate now, don't wait.
      clearTimeout(ownTimer);
      fireOnce();
    });
}

/* -------------------------------------------------------------------------- */
/*                            Small helper exports                            */
/* -------------------------------------------------------------------------- */

/** Decode a base64 confirm payload (used on confirmation page) */
export const decodeConfirmParam = (param?: string): AnyRecord | null => {
  if (!param) return null;
  try {
    if (typeof window === 'undefined') return null;
    const json = atob(param);
    return JSON.parse(json);
  } catch {
    return null;
  }
};
